/**
 * Premium primary action button.
 *
 *  ENABLED        → champagne-gold gradient on deep ink text. Subtle
 *                   inner highlight + soft shadow on hover (no MUI
 *                   bounce).
 *  DISABLED       → low-opacity ink fill, lighter text. Reads as
 *                   "intentionally muted" rather than "broken stock
 *                   control".
 *  LOADING        → button keeps its label, a small ink-tinted ring
 *                   spinner appears to its left. Cleaner than the
 *                   "Signing in…" / "Submitting…" verbal swap pattern.
 */

import { Box, Button, type ButtonProps } from '@mui/material';

const INK = '#0f0a07';
const FONT_STACK = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

interface AuthPrimaryButtonProps extends ButtonProps {
  loading?: boolean;
}

export function AuthPrimaryButton({
  loading = false,
  children,
  disabled,
  ...rest
}: AuthPrimaryButtonProps) {
  return (
    <Button
      {...rest}
      disabled={disabled || loading}
      variant="contained"
      fullWidth
      size="large"
      sx={{
        py: 1.5,
        fontFamily: FONT_STACK,
        fontSize: 14,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        borderRadius: 1.25,
        boxShadow: 'none',
        background: 'linear-gradient(135deg, #d4a84b 0%, #bb8c3f 45%, #a67a35 100%)',
        color: INK,
        position: 'relative',
        overflow: 'hidden',
        transition: 'transform 180ms ease, box-shadow 220ms ease, background 220ms ease',
        // Subtle top highlight — gives the gold a "polished metal" feel.
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '50%',
          background: 'linear-gradient(to bottom, rgba(255,255,255,0.18), rgba(255,255,255,0))',
          pointerEvents: 'none',
        },
        '&:hover': {
          background: 'linear-gradient(135deg, #e0b558 0%, #c89947 45%, #b6873d 100%)',
          boxShadow: '0 6px 18px rgba(187, 140, 63, 0.28)',
          transform: 'translateY(-1px)',
        },
        '&:active': {
          transform: 'translateY(0)',
          boxShadow: '0 3px 10px rgba(187, 140, 63, 0.22)',
        },
        '&.Mui-disabled': {
          background: 'rgba(15, 10, 7, 0.08)',
          color: 'rgba(15, 10, 7, 0.4)',
          '&::before': { display: 'none' },
        },
        ...rest.sx,
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1.25,
          position: 'relative',
          zIndex: 1,
        }}
      >
        {loading && <Spinner />}
        {children}
      </Box>
    </Button>
  );
}

/** Inline 14px gold ring spinner with a smooth 0.7s spin. */
function Spinner() {
  return (
    <Box
      aria-hidden
      sx={{
        width: 14,
        height: 14,
        borderRadius: '50%',
        border: `1.5px solid rgba(15, 10, 7, 0.18)`,
        borderTopColor: INK,
        animation: 'authBtnSpin 0.7s linear infinite',
        '@keyframes authBtnSpin': {
          '0%': { transform: 'rotate(0deg)' },
          '100%': { transform: 'rotate(360deg)' },
        },
      }}
    />
  );
}
